import React, { useEffect, useState } from "react";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import SingleSelect from "components/common/SingleSelect";
import CustomField from "components/form/CustomField";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { usePermission } from "hooks/usePermission";
import { WEEK_DAYS } from "utils/constants";
import Switch from "components/common/Switch";
import TimePicker from "components/form/TimePicker";
import { getBranchValidationSchema } from "utils/validations";
import {
  dateToTimeString,
  separateImages,
  timeStringToDate,
} from "utils/utils";
import MapPickerModal from "components/common/map/MapPickerModal";
import FileUpload from "components/common/FileUpload";
import { appendFormData, handleSubmitForm } from "helpers/apiUtils";
import {
  addBranch,
  getBranchDetails,
  updateBranch,
} from "store/slices/branchesSlice";
import { useNavigate } from "react-router-dom";
import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";

const AddEditBranch = ({ mode }) => {
  const { t } = useTranslation();
  const isEdit = mode === "edit";
  const id = useQueryParam("id");
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canAdd, canEdit } = canAction("branches");

  const { loading, isLoading, itemDetails } = useSelector(
    (state) => state.Branches,
  );
  const [showMap, setShowMap] = useState(false);

  const [initialValues, setInitialValues] = useState({
    // storeNo: "",
    name: { en: "", ar: "" },
    address: { en: "", ar: "" },
    latitude: "",
    longitude: "",
    whatsappNumber: "",
    open24Hours: true,
    googleMapLink: "",
    // trafficPercentage: 0,
    // waitTimeInMinutes: 0,
    sortOrder: 1,
    openingHours: [
      {
        day: WEEK_DAYS[0]?.value || "",
        openTime: "08:00 AM",
        closeTime: "10:00 PM",
        isClosed: false,
      },
    ],
    images: [],
  });
  const [originalImageUrls, setOriginalImageUrls] = useState([]);

  useEffect(() => {
    if (isEdit && id && canEdit) {
      dispatch(getBranchDetails(id));
    }
  }, [isEdit, id, dispatch, canEdit]);

  useEffect(() => {
    if (isEdit && itemDetails) {
      const {
        // storeNo,
        name,
        address,
        latitude,
        longitude,
        whatsappNumber,
        open24Hours,
        openingHours,
        googleMapLink,
        // trafficPercentage,
        // waitTimeInMinutes,
        sortOrder,
        imageUrls,
      } = itemDetails;
      setOriginalImageUrls(imageUrls || []);

      setInitialValues({
        // storeNo: storeNo || "",
        name: {
          en: name?.en || "",
          ar: name?.ar || "",
        },
        address: {
          en: address?.en || "",
          ar: address?.ar || "",
        },
        latitude: latitude || "",
        longitude: longitude || "",
        whatsappNumber: whatsappNumber || "",
        open24Hours: Boolean(open24Hours),
        googleMapLink: googleMapLink,
        // trafficPercentage: trafficPercentage ?? 0,
        // waitTimeInMinutes: waitTimeInMinutes ?? 0,
        sortOrder: sortOrder ?? 1,
        openingHours: open24Hours
          ? [
              {
                day: "",
                openTime: "08:00 AM",
                closeTime: "10:00 PM",
                isClosed: false,
              },
            ]
          : openingHours?.map((item) => ({
              day: item.day,
              openTime: item.openTime,
              closeTime: item.closeTime,
              isClosed: Boolean(item.isClosed),
            })) || [],
        images: imageUrls || [],
      });
    }
  }, [isEdit, itemDetails]);

  const handleSubmit = (values) => {
    const { images, openingHours, open24Hours, ...rest } = values || {};

    const allWeekDays = WEEK_DAYS.map((item) => ({
      day: item.value,
      openTime: "12:00 AM",
      closeTime: "11:59 PM",
      isClosed: false,
    }));

    const payload = {
      ...rest,

      open24Hours: String(open24Hours),

      openingHours: open24Hours ? allWeekDays : openingHours,
    };

    const data = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      appendFormData(data, key, value);
    });
    const { apiImages, newImages } = separateImages(images);

    if (apiImages.length > 0) {
      apiImages.forEach((url) => {
        data.append("imageUrls", url);
      });
    }

    // Append newly uploaded images
    if (newImages.length > 0) {
      newImages.forEach((file) => {
        data.append("images", file);
      });
    }

    if (isEdit) {
      data.append("id", id);

      handleSubmitForm({
        dispatch,
        action: updateBranch,
        payload: data,
        onSuccess: () => navigate(-1),
      });
    } else {
      handleSubmitForm({
        dispatch,
        action: addBranch,
        payload: data,
        onSuccess: () => navigate(-1),
      });
    }
  };

  if (!canEdit && isEdit) return <NoPermission />;
  if (!canAdd && !isEdit) return <NoPermission />;

  if (isEdit && loading)
    return <AddEditLoader title={t("Edit Store")} rows={3} cols={3} />;

  return (
    <div className="page-content add_branch_page">
      <PageHeader
        isBackBtn={true}
        title={isEdit ? t("Edit Store") : t("Add Store")}
      />

      <div className="common_card">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={getBranchValidationSchema(t, isEdit)}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Name (English)")}
                      name="name.en"
                      placeholder={t("Enter Name (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="name.ar"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Name (Arabic)")}
                      name="name.ar"
                      placeholder={t("Enter Name (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="name.en"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Address (English)")}
                      name="address.en"
                      placeholder={t("Enter Address (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="address.ar"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Address (Arabic)")}
                      name="address.ar"
                      placeholder={t("Enter Address (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="address.en"
                      required
                    />
                  </div>
                </Col>

                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Latitude")}
                      name="latitude"
                      placeholder="25.2048"
                      required
                    />
                  </div>
                </Col>

                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Longitude")}
                      name="longitude"
                      placeholder="55.2708"
                      required
                    />
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="form_field">
                    <button
                      type="button"
                      className="main_btn w-100 outline pick_map_btn"
                      onClick={() => setShowMap(true)}
                    >
                      {t("Pick from Map")}
                    </button>
                  </div>
                </Col>

                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("WhatsApp Number")}
                      name="whatsappNumber"
                      placeholder="+971XXXXXXXXX"
                    />
                  </div>
                </Col>

                {/* <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Traffic Percentage")}
                      name="trafficPercentage"
                      type="number"
                    />
                  </div>
                </Col> */}

                {/* <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Wait Time (Minutes)")}
                      name="waitTimeInMinutes"
                      type="number"
                    />
                  </div>
                </Col> */}

                <Col lg={4}>
                  <div className="form_field">
                    <CustomField
                      label={t("Google Map Link")}
                      name="googleMapLink"
                      type="text"
                      placeholder="https://example.com"
                      touched={touched}
                      errors={errors}
                    />
                    <p className="fs_12 mt-2 text_dark_lightest">
                      {t("Must start with http:// or https")}
                    </p>
                  </div>
                </Col>
                <Col lg={4}>
                  <div className="form_field">
                    <Switch
                      label={t("Open 24 Hours")}
                      checked={values.open24Hours}
                      handleOnChange={() =>
                        setFieldValue("open24Hours", !values.open24Hours)
                      }
                    />
                  </div>
                </Col>

                {!values.open24Hours && (
                  <>
                    <Col lg={12}>
                      <h5 className="fs_20 mt-4">{t("Opening Hours")}</h5>
                    </Col>

                    {values.openingHours.map((item, index) => (
                      <Row key={index}>
                        <Col lg={3}>
                          <div className="form_field">
                            <SingleSelect
                              required
                              label={t("Day")}
                              value={item.day}
                              options={WEEK_DAYS.filter((weekday) => {
                                const selectedDays = values?.openingHours
                                  ?.map((oh, i) =>
                                    i !== index ? oh.day : null,
                                  )
                                  ?.filter(Boolean);

                                return !selectedDays.includes(weekday.value);
                              })}
                              handleSingleSelectChange={(e) =>
                                setFieldValue(
                                  `openingHours.${index}.day`,
                                  e.value,
                                )
                              }
                              error={
                                touched?.openingHours?.[index]?.day &&
                                errors?.openingHours?.[index]?.day
                              }
                            />
                          </div>
                        </Col>

                        <Col lg={2}>
                          <div className="form_field">
                            <Switch
                              label={t("Closed")}
                              checked={item.isClosed}
                              handleOnChange={() =>
                                setFieldValue(
                                  `openingHours.${index}.isClosed`,
                                  !item.isClosed,
                                )
                              }
                            />
                          </div>
                        </Col>

                        {!item.isClosed && (
                          <>
                            <Col lg={3}>
                              <div className="form_field">
                                <label>
                                  {t("Open Time")}
                                  <span className="text-danger">*</span>
                                </label>
                                <TimePicker
                                  value={timeStringToDate(item.openTime)}
                                  onChange={(val) =>
                                    setFieldValue(
                                      `openingHours.${index}.openTime`,
                                      dateToTimeString(val),
                                    )
                                  }
                                  error={
                                    touched?.openingHours?.[index]?.openTime &&
                                    errors?.openingHours?.[index]?.openTime
                                  }
                                />
                              </div>
                            </Col>

                            <Col lg={3}>
                              <div className="form_field">
                                <label>
                                  {t("Close Time")}
                                  <span className="text-danger">*</span>
                                </label>
                                <TimePicker
                                  value={timeStringToDate(item.closeTime)}
                                  onChange={(val) =>
                                    setFieldValue(
                                      `openingHours.${index}.closeTime`,
                                      dateToTimeString(val),
                                    )
                                  }
                                  error={
                                    touched?.openingHours?.[index]?.closeTime &&
                                    errors?.openingHours?.[index]?.closeTime
                                  }
                                />
                              </div>
                            </Col>
                          </>
                        )}

                        {values.openingHours.length > 1 && (
                          <Col lg={1}>
                            <button
                              type="button"
                              className="btn btn-outline-danger close_btn"
                              onClick={() => {
                                const updated = [...values.openingHours];
                                updated.splice(index, 1);
                                setFieldValue("openingHours", updated);
                              }}
                            >
                              ✕
                            </button>
                          </Col>
                        )}
                      </Row>
                    ))}

                    <div className="g_end">
                      <button
                        type="button"
                        className="btn btn-outline-primary mt-2"
                        onClick={() =>
                          setFieldValue("openingHours", [
                            ...values.openingHours,
                            {
                              day: "",
                              openTime: "08:00 AM",
                              closeTime: "10:00 PM",
                              isClosed: false,
                            },
                          ])
                        }
                      >
                        + {t("Add Day")}
                      </button>
                    </div>
                  </>
                )}

                {isEdit && (
                  <Col lg={4}>
                    <div className="form_field">
                      <CustomField
                        label={t("Sort Order")}
                        name="sortOrder"
                        type="number"
                      />
                    </div>
                  </Col>
                )}

                <Col lg={12}>
                  <FileUpload
                    uploadedFiles={values.images || []}
                    label={t("Upload Images")}
                    setUploadedFiles={(file) => setFieldValue("images", file)}
                    multiple={true}
                    maxFiles={6}
                    maxFileSize={0.6 * 1024 * 1024}
                    acceptedFileTypes={["image/png", "image/jpeg"]}
                    required={true}
                    className={
                      touched?.images && errors.images ? "invalid" : ""
                    }
                    error={errors.images}
                  />
                </Col>

                <Col lg={12} className="d-flex justify-content-end gap-2 mt-5">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>

                  <button
                    type="submit"
                    className="main_btn"
                    disabled={isLoading}
                  >
                    {isEdit ? t("Save Changes") : t("Add Store")}
                  </button>
                </Col>
              </Row>
              <MapPickerModal
                show={showMap}
                onClose={() => setShowMap(false)}
                onSelect={({ lat, lng, address }) => {
                  setFieldValue("latitude", lat.toFixed(6));
                  setFieldValue("longitude", lng.toFixed(6));
                  setFieldValue("address.en", address);
                }}
              />
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddEditBranch;
